import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { message } from 'antd';
import { CheckCircleIcon, CreateUserIcon, FlagEntryIcon } from '../../assets/icons';
import Breadcrumbs from '../../components/Breadcrumbs';
import { keyId, nodeKey } from '../../components/OrgNodeTree';
import { useGetRolesQuery } from '../../features/roles/rolesApi';
import {
  useGetOrganizationsQuery,
  useGetMyTreeQuery,
} from '../../features/organizations/organizationsApi';
import {
  useCreateUserMutation,
  useUpdateUserMutation,
  useGetUserQuery,
  useGetDesignationQuery,
  userName,
  userEmail,
  userPhone,
  userActive,
} from '../../features/users/usersApi';
import PoliticalPersonForm from './createUser/PoliticalPersonForm';
import GovernmentOfficialForm from './createUser/GovernmentOfficialForm';
import PartyPersonForm from './createUser/PartyPersonForm';
import ClientTeamUserForm from './createUser/ClientTeamUserForm';
import { DESIGNATIONS, emptyAddress, emptyPa, type UserFormValues } from './createUser/types';

/** The four directories a user can be created under (segmented tab group, design labels). */
const DIRECTORIES = [
  { key: 'elected', label: 'Political Person' },
  { key: 'bureaucracy', label: 'Government Official' },
  { key: 'party', label: 'Party Person' },
  { key: 'organization', label: 'Client Team User' },
];

/** Pull a human-readable message out of an RTK Query / DRF error response. */
function apiErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { data?: unknown })?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    if (typeof d.detail === 'string') return d.detail;
    for (const value of Object.values(d)) {
      if (typeof value === 'string' && value.trim()) return value;
      if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
    }
  }
  return fallback;
}

/** Department is captured for the bureaucracy + organization directories. */
const hasDepartment = (directory: string) =>
  directory === 'bureaucracy' || directory === 'organization';
/** The organization directory picks a Role instead of a Location. */
const isOrgDirectory = (directory: string) => directory === 'organization';
/** Elected / bureaucracy use the ECI-PRI drill-down cascade for the location. */
const isCascadeDirectory = (directory: string) =>
  directory === 'elected' || directory === 'bureaucracy';
/** The Political Person directory renders the rich gazette form. */
const isPolitical = (directory: string) => directory === 'elected';
/** The Government Official directory renders its own flat gazette form. */
const isGovOfficial = (directory: string) => directory === 'bureaucracy';
/** The Party Person directory renders its own flat gazette form. */
const isParty = (directory: string) => directory === 'party';
/** Every directory now uses its own dedicated form and composes the name from parts. */
const usesNameSplit = (_directory: string) => true;

/** Build the `profile` object (all non-core fields) the backend stores verbatim. */
function buildProfile(v: UserFormValues): Record<string, unknown> {
  // WhatsApp mirrors a chosen mobile (whatsappIndex >= 0) or is entered manually (-1).
  const whatsappValue = v.whatsappIndex >= 0 ? (v.contact[v.whatsappIndex] ?? '') : v.whatsapp;
  const base = {
    title: v.title,
    firstName: v.firstName,
    middleName: v.middleName,
    lastName: v.lastName,
    landline: v.landline,
    landlineExt: v.landlineExt,
    whatsapp: whatsappValue,
    whatsappIndex: v.whatsappIndex,
  };
  const demographics = {
    dob: v.dob,
    gender: v.gender,
    religion: v.religion,
    caste: v.caste,
    reservation: v.reservation,
  };
  if (isPolitical(v.directory)) {
    return {
      ...base,
      ...demographics,
      designation: v.designation,
      ministerialRoles: v.ministerialRoles,
      politicalParty: v.politicalParty,
      electionResult: v.electionResult,
      addresses: v.addresses,
      pa: v.pa,
      bioSummary: v.bioSummary,
      politicalHistory: v.politicalHistory,
      markIncomplete: v.markIncomplete,
    };
  }
  if (isGovOfficial(v.directory)) {
    return { ...base, ...demographics, officeAddress: v.officeAddress, additionalRoles: v.additionalRoles };
  }
  if (isParty(v.directory)) {
    return { ...base, ...demographics };
  }
  // organization — carries the multi-role assignments (UI-only, round-tripped through the
  // profile). dob/gender are part of the shared profile shape (empty here — no inputs).
  return {
    ...base,
    dob: v.dob,
    gender: v.gender,
    assignedRoles: v.assignedRoles,
  };
}

/** Directory groups used by the validation rules. */
const NAME_LAST_DIRS = ['elected', 'bureaucracy', 'party']; // show a required Last Name
const DEMOGRAPHIC_DIRS = ['elected', 'bureaucracy', 'party']; // required DOB + Gender
const CASCADE_DIRS = ['elected', 'bureaucracy', 'organization']; // EC location directories

// Mandatory fields (per the current policy): First Name, Last Name, Mobile Number, DOB and
// Gender. Everything else — email, location, designation, roles, reservation, boundary level —
// is optional. Last Name / DOB / Gender are only enforced where those inputs exist (the
// Client Team form has no last-name/DOB/gender fields, so those directories are exempt).
const validationSchema = Yup.object({
  organization: Yup.mixed().test('required', 'Select an organization', (v) => v !== '' && v != null),
  // fullName is composed from parts for every directory, so it's never required directly.
  firstName: Yup.string().trim().required('This field is required'),
  lastName: Yup.string().when('directory', {
    is: (d: string) => NAME_LAST_DIRS.includes(d),
    then: (s) => s.trim().required('Last name is required'),
    otherwise: (s) => s,
  }),
  gender: Yup.string().when('directory', {
    is: (d: string) => DEMOGRAPHIC_DIRS.includes(d),
    then: (s) => s.required('Select a gender'),
    otherwise: (s) => s,
  }),
  dob: Yup.string().when('directory', {
    is: (d: string) => DEMOGRAPHIC_DIRS.includes(d),
    then: (s) => s.required('Date of birth is required'),
    otherwise: (s) => s,
  }),
  // Optional — validate the format only when a value is present.
  email: Yup.string().trim().email('Enter a valid email address'),
  contact: Yup.array()
    .of(Yup.string())
    .test('required', 'Mobile number is required', (a) => (a ?? []).some((v) => (v || '').trim()))
    .test('valid', 'Enter valid 10-digit mobile numbers', (a) =>
      (a ?? []).every((v) => !v || /^\d{10}$/.test((v || '').trim())),
    ),
});

const INITIAL_VALUES: UserFormValues = {
  directory: 'elected',
  organization: '',
  fullName: '',
  email: '',
  contact: [''],
  location: '',
  role: '',
  designation: [],
  department: [],
  active: true,
  title: '',
  firstName: '',
  middleName: '',
  lastName: '',
  landline: '',
  landlineExt: '',
  whatsapp: '',
  whatsappIndex: -1,
  politicalParty: '',
  electionResult: '',
  designationValue: '',
  ministerialRoles: [],
  dob: '',
  gender: '',
  religion: '',
  caste: '',
  reservation: '',
  addresses: {
    constituencyHome: emptyAddress(),
    constituencyOffice: emptyAddress(),
    capitalHome: emptyAddress(),
    capitalOffice: emptyAddress(),
  },
  pa: { primary: emptyPa(), secondary: emptyPa() },
  bioSummary: '',
  politicalHistory: '',
  markIncomplete: false,
  officeAddress: '',
  additionalRoles: [],
  roleClassification: 'territorial',
  boundaryAuthority: 'eci',
  boundaryLevel: '',
  assignedRoles: [],
};

/** Valid directory keys a create screen can be pre-scoped to (via `?directory=`). */
const DIRECTORY_KEYS = ['elected', 'bureaucracy', 'party', 'organization'];

export default function CreateUserPage({ directory }: { directory?: string } = {}) {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = Boolean(editId);
  // The directory is chosen by which page you came from (sidebar → Directory group) via a
  // dedicated route (e.g. /admin/users/political/new); the in-form "Select Directory" picker
  // was removed.
  const initialDirectory = DIRECTORY_KEYS.includes(directory ?? '')
    ? (directory as string)
    : 'elected';

  const { data: editUser } = useGetUserQuery(editId!, { skip: !isEdit });
  const [savedCascade, setSavedCascade] = useState<{ label?: string } | null>(null);
  const [locationRank, setLocationRank] = useState<number | undefined>();
  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const saving = isCreating || isUpdating;

  const { data: orgsData } = useGetOrganizationsQuery({ all: true });
  const { data: myTree } = useGetMyTreeQuery();
  const { data: rolesData } = useGetRolesQuery({ all: true });
  const roleOptions = (rolesData?.items ?? []).map((r) => ({ value: r.id, label: r.name }));

  const formik = useFormik<UserFormValues>({
    initialValues: { ...INITIAL_VALUES, directory: initialDirectory },
    validationSchema,
    onSubmit: async (values) => {
      const composedName = usesNameSplit(values.directory)
        ? [values.title, values.firstName, values.middleName, values.lastName]
            .map((s) => s.trim())
            .filter(Boolean)
            .join(' ')
        : values.fullName.trim();
      // Political Person + Party Person use the Designation/Party-Role tag field (`designation`);
      // Political's ministerial roles ride along separately in `profile.ministerialRoles`.
      // Government / Client Team map their single Designation/Cadre field to `designationValue`.
      const designationList =
        isPolitical(values.directory) || isParty(values.directory)
          ? values.designation.map((s) => s.trim()).filter(Boolean)
          : [values.designationValue.trim()].filter(Boolean);
      const payload = {
        directory: values.directory,
        fullName: composedName,
        email: values.email.trim(),
        contact: values.contact.map((c) => c.trim()).filter(Boolean),
        organization: values.organization === '' ? undefined : Number(values.organization),
        designation: designationList.length ? designationList : undefined,
        ...(hasDepartment(values.directory)
          ? { department: values.department.length ? values.department : undefined }
          : {}),
        // Client Team User's roles are managed via the UI-only "Assigned Roles & Scopes"
        // manager (carried in profile), so no single role/location is sent for organization.
        // Cascade directories send the EC area code (+ level rank); party — and a Government
        // Official whose department has its own hierarchy — send the org-node id instead.
        ...(isOrgDirectory(values.directory)
          ? { location: undefined }
          : isGovOfficial(values.directory) && hierarchyKind
            ? { location: values.location ? keyId(values.location) : null }
            : isCascadeDirectory(values.directory)
              ? {
                  location: values.location || undefined,
                  ...(locationRank != null ? { ec_location_level_id: locationRank } : {}),
                }
              : { location: values.location ? keyId(values.location) : null }),
        active: values.active,
        // All non-core gazette fields — stored & returned by the backend as-is.
        profile: buildProfile(values),
      };
      try {
        if (isEdit && editId) {
          await updateUser({ id: editId, patch: { ...payload, is_active: values.active } }).unwrap();
        } else {
          await createUser(payload).unwrap();
        }
        message.success(isEdit ? 'User updated.' : 'User created.');
        navigate('/admin/users');
      } catch (err) {
        message.error(
          apiErrorMessage(err, `Could not ${isEdit ? 'update' : 'create'} the user. Please try again.`),
        );
      }
    },
  });

  const { values, errors, touched, handleChange, handleBlur, setFieldValue } = formik;
  const political = isPolitical(values.directory);
  const govOfficial = isGovOfficial(values.directory);
  const party = isParty(values.directory);

  // The dedicated forms have no org selector — resolve the admin's own org (falls back to
  // the first org) so the location trees have a scope.
  const resolvedOrg = myTree?.organization ?? orgsData?.items?.[0]?.id;
  useEffect(() => {
    if (values.organization === '' && resolvedOrg != null && !isEdit) {
      setFieldValue('organization', resolvedOrg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.organization, resolvedOrg, isEdit]);

  const treeOrg = values.organization === '' ? undefined : Number(values.organization);

  // Distinct department values for the org (Government Official Select options).
  // Designation / department options from the backend (field_options), scoped by directory
  // (and org when known). Falls back to the static sample list when the API has none yet.
  // The department scopes the designations (and reports its own hierarchy_kind) for the
  // directories that capture a department.
  const selectedDepartment = hasDepartment(values.directory) ? values.department[0] : undefined;
  const { data: fieldOptions } = useGetDesignationQuery({
    directory: values.directory,
    organization: treeOrg,
    department: selectedDepartment,
  });
  const designationOptions = (
    fieldOptions?.designations?.length ? fieldOptions.designations : DESIGNATIONS
  ).map((v) => ({ value: v, label: v }));
  const departmentOptions = (fieldOptions?.departments ?? []).map((v) => ({ value: v, label: v }));
  // When the chosen department has its own hierarchy, the Government Official location is picked
  // from that node tree — so it's saved as an org-node id, not an EC area code.
  const hierarchyKind = selectedDepartment ? (fieldOptions?.hierarchy_kind ?? undefined) : undefined;

  // Edit mode: prefill the form once the user loads.
  useEffect(() => {
    if (!editUser) return;
    const dir = editUser.directory || 'elected';
    setFieldValue('directory', dir);
    if (editUser.organization != null) setFieldValue('organization', editUser.organization);
    const name = userName(editUser) === '—' ? '' : userName(editUser);
    setFieldValue('fullName', name);
    // Best-effort split for the gazette layouts (first token → first name, rest → last name).
    if (usesNameSplit(dir) && name) {
      const parts = name.split(/\s+/);
      setFieldValue('firstName', parts[0] ?? '');
      setFieldValue('lastName', parts.slice(1).join(' '));
    }
    setFieldValue('email', userEmail(editUser));
    const eContacts = editUser as {
      contact?: unknown;
      contacts?: unknown;
      phone?: unknown;
      phone_numbers?: unknown;
    };
    const toContacts = (v: unknown): string[] => {
      if (Array.isArray(v)) {
        return v
          .map((x) => {
            const s =
              x && typeof x === 'object'
                ? String(
                    (x as { number?: string; phone?: string; value?: string }).number ??
                      (x as { phone?: string }).phone ??
                      (x as { value?: string }).value ??
                      '',
                  )
                : String(x);
            return s.replace(/\D/g, '');
          })
          .filter(Boolean);
      }
      if (typeof v === 'string') return v.split(',').map((s) => s.replace(/\D/g, '')).filter(Boolean);
      return [];
    };
    const firstNonEmpty = (...arrs: string[][]) => arrs.find((a) => a.length) ?? [];
    let contacts = firstNonEmpty(
      toContacts(eContacts.contact),
      toContacts(eContacts.contacts),
      toContacts(eContacts.phone),
      toContacts(eContacts.phone_numbers),
    );
    if (!contacts.length) contacts = [userPhone(editUser).replace(/\D/g, '')].filter(Boolean);
    setFieldValue('contact', contacts.length ? contacts : ['']);
    // Prefer the list column; otherwise split the joined string ("MLA, ddd" → tags).
    const toTags = (list: string[] | null | undefined, joined: string | string[] | null | undefined): string[] => {
      if (Array.isArray(list) && list.length) return list.filter(Boolean);
      if (Array.isArray(joined)) return joined.filter(Boolean);
      return typeof joined === 'string'
        ? joined.split(',').map((s) => s.trim()).filter(Boolean)
        : [];
    };
    const desig = toTags(editUser.designations, editUser.designation);
    setFieldValue('designation', desig);
    setFieldValue('department', toTags(editUser.departments, editUser.department));
    if (dir === 'elected') {
      // Designation tags come from the core `designation` (set above); ministerial roles are
      // restored from `profile.ministerialRoles` below.
      setFieldValue('ministerialRoles', []);
    } else if (dir !== 'party') {
      // Cadre (bureaucracy) / Designation (organization) — a single value. Party Role is a
      // tag field, so it stays on the `designation` array set above.
      setFieldValue('designationValue', desig.join(', '));
    }
    setFieldValue('active', userActive(editUser));
    // Elected / Government / Client Team store an EC area code (+ level); Party stores an
    // org-node id — so only Party takes the org-node prefill path.
    if (CASCADE_DIRS.includes(dir)) {
      const loc = editUser.location as
        | number
        | string
        | {
            code?: string;
            areaCode?: string;
            area_code?: string;
            name?: string;
            label?: string;
            levelRank?: number;
            level_rank?: number;
            rank?: number;
            ec_location_level_id?: number;
          }
        | null
        | undefined;
      const asObj = loc && typeof loc === 'object' ? loc : null;
      const eu = editUser as {
        location_rank?: number;
        location_level_rank?: number;
        location_level_id?: number;
        ec_location_level_id?: number;
        area_code?: string;
      };
      const code =
        asObj != null
          ? String(asObj.code ?? asObj.areaCode ?? asObj.area_code ?? '')
          : loc != null
            ? String(loc)
            : String(eu.area_code ?? '');
      const rankRaw =
        asObj?.levelRank ??
        asObj?.level_rank ??
        asObj?.rank ??
        asObj?.ec_location_level_id ??
        eu.location_rank ??
        eu.location_level_rank ??
        eu.location_level_id ??
        eu.ec_location_level_id;
      const label = asObj?.name || asObj?.label || editUser.location_name || code || undefined;
      setFieldValue('location', code);
      if (rankRaw != null) setLocationRank(Number(rankRaw));
      setSavedCascade({ label });
    } else if (editUser.location != null) {
      setFieldValue('location', nodeKey(editUser.location));
    }
    const firstRole = (editUser.roles ?? [])
      .map((r) => (typeof r === 'object' ? (typeof r.role === 'object' ? r.role?.id : r.role) : undefined))
      .find((v): v is number => typeof v === 'number');
    if (firstRole != null) setFieldValue('role', firstRole);

    // Repopulate the non-core gazette fields from the stored `profile` (exact values,
    // overriding the best-effort name split above).
    const prof = editUser.profile;
    if (prof && typeof prof === 'object') {
      const p = prof as Record<string, unknown>;
      const str = (k: string) => (typeof p[k] === 'string' ? (p[k] as string) : '');
      const setIf = (field: keyof UserFormValues, k: string) => {
        if (p[k] !== undefined && p[k] !== null && p[k] !== '') setFieldValue(field, str(k));
      };
      (
        [
          'title',
          'firstName',
          'middleName',
          'lastName',
          'landline',
          'landlineExt',
          'whatsapp',
          'politicalParty',
          'electionResult',
          'dob',
          'gender',
          'religion',
          'caste',
          'reservation',
          'bioSummary',
          'politicalHistory',
          'officeAddress',
          'roleClassification',
          'boundaryAuthority',
          'boundaryLevel',
        ] as (keyof UserFormValues)[]
      ).forEach((k) => setIf(k, k as string));
      if (typeof p.whatsappIndex === 'number') setFieldValue('whatsappIndex', p.whatsappIndex);
      if (typeof p.markIncomplete === 'boolean') setFieldValue('markIncomplete', p.markIncomplete);
      if (Array.isArray(p.designation))
        setFieldValue('designation', p.designation.map(String).filter(Boolean));
      if (Array.isArray(p.ministerialRoles))
        setFieldValue('ministerialRoles', p.ministerialRoles.map(String).filter(Boolean));
      if (Array.isArray(p.additionalRoles))
        setFieldValue('additionalRoles', p.additionalRoles.map(String).filter(Boolean));
      if (Array.isArray(p.assignedRoles))
        setFieldValue(
          'assignedRoles',
          (p.assignedRoles as import('./createUser/types').AssignedRole[]).filter(
            (r) => r && typeof r === 'object',
          ),
        );
      if (p.addresses && typeof p.addresses === 'object') {
        const a = p.addresses as Record<string, Partial<import('./createUser/types').AddressValues>>;
        setFieldValue('addresses', {
          constituencyHome: { ...emptyAddress(), ...(a.constituencyHome ?? {}) },
          constituencyOffice: { ...emptyAddress(), ...(a.constituencyOffice ?? {}) },
          capitalHome: { ...emptyAddress(), ...(a.capitalHome ?? {}) },
          capitalOffice: { ...emptyAddress(), ...(a.capitalOffice ?? {}) },
        });
      }
      if (p.pa && typeof p.pa === 'object') {
        const pa = p.pa as Record<string, Partial<import('./createUser/types').PaValues>>;
        setFieldValue('pa', {
          primary: { ...emptyPa(), ...(pa.primary ?? {}) },
          secondary: { ...emptyPa(), ...(pa.secondary ?? {}) },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editUser]);

  const errorOf = (name: keyof UserFormValues) =>
    touched[name] && errors[name] ? (errors[name] as string) : undefined;

  return (
    <div className="flex flex-col gap-6">
      {/* Breadcrumb + heading */}
      <div className="flex flex-col gap-1">
        <Breadcrumbs
          items={[
            { label: 'Dashboard', to: '/admin' },
            { label: 'Users', to: '/admin/users' },
            { label: isEdit ? 'Edit User' : 'Create New User' },
          ]}
        />
        <h1 className="pt-1 text-2xl font-semibold leading-10 tracking-[-0.64px] text-[#131b2e]">
          {isEdit ? 'Edit User' : 'Create New User'}
        </h1>
        <p className="text-base text-[#434655]">
          Register a new administrator or regional officer to the CHUNAAV platform.
        </p>
      </div>

      {/* Form card */}
      <form
        onSubmit={formik.handleSubmit}
        noValidate
        className="rounded-xl border border-[#c3c6d7]/30 bg-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_4px_6px_-2px_rgba(0,0,0,0.05)]"
      >
        {/* Card header */}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-t-xl border-b border-[#c3c6d7]/30 bg-white px-6 py-5">
          <div className="flex items-center gap-3">
            <span className="flex size-10 items-center justify-center rounded-lg bg-[#eff6ff] text-brand">
              <CreateUserIcon className="size-5" />
            </span>
            <h2 className="text-xl font-semibold tracking-[-0.2px] text-[#131b2e]">User Details</h2>
          </div>
          <div className="flex items-center gap-2">
            {political && (
              <>
                <span className="rounded-full border border-[#e2e8f0] bg-[#f1f5f9] px-3 py-1 text-[11px] font-medium leading-[16.5px] text-[#475569]">
                  Status: {values.markIncomplete ? 'Unverified' : 'Draft'}
                </span>
                <span className="flex items-center gap-1.5 rounded-lg border border-[#fde68a] bg-[#fffbeb] px-3 py-1 text-[10px] font-semibold text-[#d97706]">
                  <FlagEntryIcon className="size-3.5" />
                  Flag Entry
                </span>
              </>
            )}
            <span className="text-[11px] font-bold uppercase leading-[16.5px] tracking-[0.55px] text-[#94a3b8]">
              Record #{editId ?? '842'}
            </span>
          </div>
        </div>

        {/* Form body */}
        <div className="flex flex-col gap-6 px-8 pb-8 pt-6">
          {/* Directory-specific body — the directory is set by the route (?directory=), so the
              in-form "Select Directory" picker was removed. */}
          {political ? (
            <PoliticalPersonForm
              values={values}
              setFieldValue={setFieldValue}
              handleChange={handleChange}
              handleBlur={handleBlur}
              errorOf={errorOf}
              organization={treeOrg}
              designationOptions={designationOptions}
              savedCascade={savedCascade}
              onLocationRank={setLocationRank}
            />
          ) : govOfficial ? (
            <GovernmentOfficialForm
              values={values}
              setFieldValue={setFieldValue}
              handleChange={handleChange}
              handleBlur={handleBlur}
              errorOf={errorOf}
              organization={treeOrg}
              designationOptions={designationOptions}
              departmentOptions={departmentOptions}
              hierarchyKind={hierarchyKind}
              savedCascade={savedCascade}
              onLocationRank={setLocationRank}
            />
          ) : party ? (
            <PartyPersonForm
              values={values}
              setFieldValue={setFieldValue}
              handleChange={handleChange}
              handleBlur={handleBlur}
              errorOf={errorOf}
              organization={treeOrg}
              designationOptions={designationOptions}
            />
          ) : (
            <ClientTeamUserForm
              values={values}
              setFieldValue={setFieldValue}
              handleChange={handleChange}
              handleBlur={handleBlur}
              errorOf={errorOf}
              organization={treeOrg}
              roleOptions={roleOptions}
              departmentOptions={departmentOptions}
              designationOptions={designationOptions}
            />
          )}

          {/* Account Status banner — Political Person carries this inside section 7. */}
          {!political && (
            <div className="flex items-center justify-between gap-4 rounded-xl border border-[#dce9fa] bg-[#f0f6fe] px-[21px] py-4">
              <div className="flex items-center gap-2.5">
                <span className="size-2.5 shrink-0 rounded-full bg-[#2563eb]" />
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-semibold text-[#1e293b]">Account Status</span>
                  <span className="text-xs text-[#64748b]">
                    User account will be provisioned as{' '}
                    <span className="font-bold text-[#2563eb]">
                      {values.active ? 'Active' : 'Inactive'}
                    </span>
                  </span>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={values.active}
                onClick={() => setFieldValue('active', !values.active)}
                className={[
                  'relative h-6 w-11 shrink-0 rounded-full transition-colors',
                  values.active ? 'bg-[#2563eb]' : 'bg-[#c2c6d6]',
                ].join(' ')}
              >
                <span
                  className={[
                    'absolute top-0.5 size-5 rounded-full bg-white transition-all',
                    values.active ? 'left-[22px]' : 'left-0.5',
                  ].join(' ')}
                />
              </button>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap items-center justify-end gap-4 pt-2">
            <button
              type="button"
              onClick={() => navigate('/admin/users')}
              className="rounded-lg border border-line px-[33px] py-3 text-sm font-semibold text-ink-secondary transition-colors hover:bg-brand-shade"
            >
              Cancel
            </button>
            {political && (
              <button
                type="button"
                disabled={saving}
                onClick={() => {
                  setFieldValue('markIncomplete', true);
                  formik.submitForm();
                }}
                className="rounded-lg border border-[#f59e0b] bg-[#fffbeb] px-6 py-3 text-sm font-semibold text-[#b45309] transition-colors hover:bg-[#fef3c7] disabled:cursor-not-allowed disabled:opacity-60"
              >
                Save as Flagged Entry
              </button>
            )}
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 rounded-lg bg-brand px-10 py-3 text-sm font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving
                ? 'Saving…'
                : isEdit
                  ? 'Update User'
                  : political
                    ? 'Register Representative'
                    : govOfficial
                      ? 'Register Government Official'
                      : party
                        ? 'Register Party Member'
                        : 'Register User'}
              <CheckCircleIcon className="size-[15px]" />
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
